// src/pages/ProjectsPage.jsx
import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaArrowRight } from 'react-icons/fa';
import './ProjectsPage.css';

const fullProjects = [
    {
    title: 'PostTrain',
    type: 'Research Project',
    link: 'https://github.com/nikhil-405/PostTrain',
    topics: ['RLHF', 'LLMs', 'Alignment'],
    description:
      'Implemented various LLM PostTraining methods from scratch along with their Collators, Processors and Trainers. Repository includes optimized implementations of SFT, DPO, PPO, GRPO, etc. ',
  },
  {
    title: 'XAI for Transformers: Interpolated Propagation',
    type: 'Research Project',
    link: 'https://github.com/nikhil-405/XAI_for_Transformers',
    topics: ['XAI', 'Transformers', 'Interpretability'],
    description:
      'Extends conservative propagation with an interpolated scheme that trades off sensitivity and conservation; includes GammaNet, a learnable γ-scheduler that improves AUAC/AUMSE while preserving completeness.',
  },
  {
    title: 'Lottery Ticket Hypothesis',
    type: 'Research Project',
    link: 'https://github.com/aditya-me13/lottery_ticket_hypothesis',
    topics: ['Pruning', 'Sparse Networks', 'Paper Re-Implementation'],
    description:
      'Iterative magnitude pruning to find sparse “winning tickets” that retrain nearly as well as the dense model; includes runners for MNIST/Fashion/CIFAR-10.',
  },
  {
    title: 'Deep Motion Track: ULM Framework',
    type: 'Research Project',
    link: 'https://github.com/nikhil-405/DeepMotionTrack/tree/main',
    topics: ['Medical Imaging', 'Deep Learning', 'Computer Vision'],
    description:
      'Attention‑based denoising + motion‑guided tracking for Ultrasound Localization Microscopy. I build a DL + signal‑processing pipeline that denoises frames, localizes microbubbles, and assigns tracks robustly across clutter.',
  },
  {
    title: 'AtomML',
    type: 'Library',
    link: 'https://github.com/nikhil-405/atomml',
    topics: ['Deep Learning', 'Python', 'NumPy'],
    description:
      'A from‑scratch ML library built on NumPy/Pandas. Includes classic algorithms and simple neural nets with a clean, testable API—no scikit‑learn or PyTorch under the hood.',
  },
  {
    title: 'Hyperbolic Graph Retrieval',
    type: 'Research Project',
    link: 'https://github.com/nikhil-405/hyperbolic-graph-retrieval',
    topics: ['Graph Representation Learning', 'Large Scale Retrieval', 'Hyperbolic Transformers'],
    description:
      'Scalable graph search using hyperbolic embeddings. A custom HypFormer encoder and GAN‑style hashing produce compact codes for fast Hamming retrieval with hyperbolic re‑ranking.',
  },
  {
    title: 'Indic OCR',
    type: 'Research Project',
    link: 'https://github.com/nikhil-405/indic-ocr/',
    topics: ['OCR', 'Computer Vision'],
    description:
      'Language‑agnostic OCR for Devanagari and legacy (non‑Unicode) Hindi fonts. Uses synthetic data and script‑aware modeling to handle ligatures and conjuncts.',
  },
  {
    title: 'MusicRecs',
    type: 'WebApp',
    link: 'https://github.com/nikhil-405/musicrecs',
    topics: ['Web Development', 'Flask', 'API Integration'],
    description:
      'A Flask app that recommends songs using precomputed audio embeddings and the Spotify API. Search by track/artist, sign in with Spotify, and get similar picks.',
  },
  {
    title: 'Spanish Language Model',
    type: 'ML Pipeline',
    link: 'https://github.com/nikhil-405/NLP_data_curation',
    topics: ['NLP', 'Transformers', 'Data Curation'],
    description:
      'An end‑to‑end pipeline that scrapes Spanish news sites, cleans and deduplicates text, tokenizes it, and pretrains a ~50M‑parameter Transformer from scratch.',
  },
  {
    title: 'Parallelized Neural Network inference on FPGA',
    type: 'Hardware Project',
    link: 'https://github.com/nikhil-405/fcl-fpga-inference',
    topics: ['FPGA', 'Hardware Acceleration', 'Verilog'],
    description:
      'Verilog implementation of fully‑connected layer inference on a Basys 3 FPGA. Parallelized matrix multiply and COE weight conversion deliver a tangible speedup over a CPU baseline.',
  },
  {
    title: 'Software Tools & Techniques for AI',
    type: 'Course Collection',
    link: 'https://github.com/nikhil-405?tab=repositories&q=stt-ai',
    topics: ['MLOps', 'Data Engineering', 'Docker'],
    description:
      'Course lab series covering real‑world ML ops: logging/validation, data labeling and augmentation, hyperparameter sweeps, PyTorch training, and Docker + GCP deployment.',
  },
  {
    title: 'Disk Canvas',
    type: 'Desktop App',
    link: 'https://github.com/nikhil-405/DiskCanvas',
    topics: ['Data Visualization', 'File Systems', 'C++'],
    description:
      'A C++ desktop utility that visualizes your file system as an interactive hierarchy so you can see where disk space actually goes.',
  },
  {
    title: 'Image Captioning',
    type: 'Deep Learning',
    link: 'https://github.com/nikhil-405/image-captioning',
    topics: ['Computer Vision', 'CNN', 'LSTM'],
    description:
      "CNN‑LSTM captioning: Inception for visual features, an ~10M‑param LSTM for text. Trained on Flickr30k; weights available on Hugging Face for quick inference.",
  },
  {
    title: 'Gray2Color',
    type: 'Computer Vision Tool',
    link: 'https://github.com/nikhil-405/g2c',
    topics: ['Paper Re-Implementation', 'GANs', 'PyTorch'],
    description:
      'PyTorch colorization that predicts among 313 Lab a/b clusters to turn grayscale into plausible color. Comes with training + inference notebooks and published weights.',
  },
  {
    title: 'RL Algorithms Benchmark',
    type: 'Research Project',
    link: 'https://github.com/nikhil-405/RL-Algorithms',
    topics: ['Reinforcement Learning', 'Evaluation',],
    description:
      'Side-by-side experiments comparing classic RL algorithms across environments of BeamRider, and a custom Connect4 environment.',
  },
];

export default function ProjectsPage() {
  const [query, setQuery] = useState('');

  const filteredProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return fullProjects;
    return fullProjects.filter((p) => {
      const haystack = [p.title || '', p.type || '', p.description || '', ...(p.topics || [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  return (
    <>
      <Helmet>
        <title>Projects - Nikhil Goyal</title>
        <meta name="description" content="Explore Nikhil Goyal’s AI projects in NLP, computer vision, OCR, machine learning, and medical imaging, including Ultrasound Localization Microscopy and Indic OCR." />
        <meta name="keywords" content="Nikhil Goyal projects, AI projects, machine learning projects, computer vision projects, NLP projects, OCR projects, medical imaging projects, ultrasound localization microscopy, Indic OCR, deep learning projects, IIT Gandhinagar" />
        <meta name="author" content="Nikhil Goyal" />
        <link rel="canonical" href="https://nikhil-405.github.io/projects" />
        <meta name="robots" content="index, follow, max-image-preview:large" />

        {/* Open Graph */}
        <meta property="og:title" content="Projects - Nikhil Goyal" />
        <meta property="og:description" content="Portfolio of AI and machine learning projects by Nikhil Goyal covering NLP, computer vision, OCR, medical imaging, and deep learning." />
        <meta property="og:url" content="https://nikhil-405.github.io/projects" />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Projects - Nikhil Goyal" />
        <meta name="twitter:description" content="Portfolio of AI and machine learning projects by Nikhil Goyal covering NLP, OCR, computer vision, and medical imaging." />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Nikhil Goyal - AI & ML Projects',
            description: 'Portfolio of AI and machine learning projects by Nikhil Goyal spanning NLP, computer vision, OCR, and medical imaging.',
            url: 'https://nikhil-405.github.io/projects',
            author: { '@type': 'Person', name: 'Nikhil Goyal', url: 'https://nikhil-405.github.io/' },
            mainEntity: {
              '@type': 'ItemList',
              name: 'AI & ML Projects',
              itemListElement: [
                {
                  '@type': 'SoftwareSourceCode',
                  name: 'Ultrasound Localization Microscopy',
                  description: 'Deep learning solutions for medical imaging and microbubble tracking',
                  programmingLanguage: 'Python',
                  applicationCategory: 'Medical Imaging',
                },
                {
                  '@type': 'SoftwareSourceCode',
                  name: 'Indic OCR',
                  description: 'Advanced OCR system for Indian languages',
                  programmingLanguage: 'Python',
                  applicationCategory: 'Natural Language Processing',
                },
              ],
            },
          })}
        </script>
      </Helmet>

      <main className="projects-page">
        <h2 className="projects-page-title">All Projects</h2>

        {/* Search Bar */}
        <div className="projects-search" role="search">
          <input
            type="text"
            className="projects-search-input"
            placeholder="Search by title, topic, or description..."
            aria-label="Search projects"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="projects-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              title="Clear"
            >
              Clear
            </button>
          )}
        </div>

        <div className="project-list">
          {filteredProjects.map((proj) => {
            const hasLink = proj.link && proj.link.trim() !== '';
            if (hasLink) {
              return (
                <a
                  key={proj.title}
                  href={proj.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-card"
                >
                  <FaArrowRight className="project-card-arrow" />
                  <h3>{proj.title}</h3>
                  <span className="project-type">{proj.type}</span>
                  <p>{proj.description}</p>
                  <div className="topic-tags">
                    {proj.topics.map((topic, index) => (
                      <span key={index} className="topic-tag">
                        {topic}
                      </span>
                    ))}
                  </div>
                </a>
              );
            }
            return (
              <div key={proj.title} className="project-card project-card-no-link">
                <h3>{proj.title}</h3>
                <span className="project-type">{proj.type}</span>
                <p>{proj.description}</p>
                <div className="topic-tags">
                  {proj.topics.map((topic, index) => (
                    <span key={index} className="topic-tag">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
          {filteredProjects.length === 0 && (
            <div className="projects-empty">No projects match your search.</div>
          )}
        </div>
      </main>
    </>
  );
}